import { Eye, ScanEyeIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Pagination from "../../components/ui/dashboard/Pagination";
import { CurrentLocation } from "../../components/ui/dashboard/StatusCommon";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import {
  formatCurrencyForTable,
  formatDateDDMMYYYY,
  formatSignedNumber,
} from "../../lib/utils";
import {
  Dispatch,
  DispatchSearchRequest,
  DispatchSearchResponse,
} from "./@types";
import DispatchHistoryModal from "./components/DispatchHistoryModal";
import DispatchFilterComponent from "./components/ui/DispatchFilterComponent";
import { useSearchDispatchesMutation } from "./dispatchApiSlice";

const DispatchHistory = () => {
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [viewDispatch, setViewDispatch] = useState<Dispatch | null>(null);
  const [openModal, setOpenModal] = useState<string | null>(null);

  const navigate = useNavigate();

  const [filterData, setFilterData] = useState({
    startDate: "",
    endDate: "",
    category: "",
  });

  // RTK Query mutation
  const [getDispatchHistory, { data: dispatchHistoryData, isLoading, error: loadError }] =
    useSearchDispatchesMutation();

  const fetchDispatchHistory = useCallback(async () => {
    const requestBody: DispatchSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      startDate: filterData.startDate || undefined,
      endDate: filterData.endDate || undefined,
      vehicleId: id,
    };

    try {
      await getDispatchHistory(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch dispatch history:", err);
    }
  }, [currentPage, pageSize, filterData, getDispatchHistory]);

  useEffect(() => {
    fetchDispatchHistory();
  }, [currentPage, pageSize, filterData, fetchDispatchHistory]);

  const columns: Column<Dispatch>[] = [
    {
      key: "dispatchNumber",
      header: "Trip number",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "tripStartTime",
      header: "Date",

      render: (value: string) => (
        <span className="text-hColor capitalize">
          {formatDateDDMMYYYY(value)}
        </span>
      ),
    },
    {
      key: "crews",
      header: "Crew member",

      render: (value: any, item: Dispatch) => (
        <span className="text-hColor">
          {item.crews?.length
            ? `${item.crews[0].name}, +${item.crews.length} more`
            : "No crew"}
        </span>
      ),
    },
    {
      key: "totalExpense",
      header: "Expenses (NGN)",

      render: (value: number) => (
        <span className="text-red-600">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "totalIncomeEarned",
      header: "Income (NGN)",

      render: (value: number) => (
        <span className="text-green-600">{formatCurrencyForTable(value)}</span>
      ),
    },
    {
      key: "netBanked",
      header: "Net banked (NGN)",

      render: (value: number) => (
        <span className="text-green-700">{formatSignedNumber(value)}</span>
      ),
    },
    {
      key: "approximateDistance",
      header: "Distance (KM)",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "tripStatus",
      header: "Status",

      render: (value: string) => <CurrentLocation location={value} />,
    },
    {
      key: "actions",
      header: "Action",

      align: "center" as const,
      render: (value: any, item: Dispatch) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View dispatch",
                onClick: () => {
                  setViewDispatch(item);
                  setOpenModal("view");
                },
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: "Dispatch details",
                onClick: () => navigate(`/admin/dispatch-details/${item.id}`),
                icon: <ScanEyeIcon className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const response = dispatchHistoryData?.data as
    | DispatchSearchResponse
    | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="text-pColor text-[13px] flex flex-wrap items-center gap-x-4 gap-y-1 bg-white rounded-[10px] p-4">
          <button onClick={() => navigate("/admin/vehicles")}>
            All vehicles
          </button>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="24"
              viewBox="0 0 12 24"
            >
              <defs>
                <path
                  id="SVG1pzpbdYY"
                  fill="currentColor"
                  d="m7.588 12.43l-1.061 1.06L.748 7.713a.996.996 0 0 1 0-1.413L6.527.52l1.06 1.06l-5.424 5.425z"
                />
              </defs>
              <use
                fillRule="evenodd"
                href="#SVG1pzpbdYY"
                transform="rotate(-180 5.02 9.505)"
              />
            </svg>
          </div>
          <button onClick={() => navigate(`/admin/dispatch/${id}`)}>
            {dispatchHistoryData?.data.data[0]?.vehicleModel}
          </button>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="24"
              viewBox="0 0 12 24"
            >
              <defs>
                <path
                  id="SVG1pzpbdYY"
                  fill="currentColor"
                  d="m7.588 12.43l-1.061 1.06L.748 7.713a.996.996 0 0 1 0-1.413L6.527.52l1.06 1.06l-5.424 5.425z"
                />
              </defs>
              <use
                fillRule="evenodd"
                href="#SVG1pzpbdYY"
                transform="rotate(-180 5.02 9.505)"
              />
            </svg>
          </div>
          <button className="font-semibold text-hColor">
            Dispatch history
          </button>
        </div>

        {/* All drivers section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Filters and Actions */}
          <div className="flex items-center space-x-2">
            <h1>Dispatch history</h1>
            <DispatchFilterComponent onApplyFilters={setFilterData} />
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No dispatch history found"
            />
          </div>
        </div>
        <div className="">
          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>

      {openModal === "view" && viewDispatch && (
        <DispatchHistoryModal
          dispatch={viewDispatch}
          isOpen={openModal === "view"}
          onClose={() => setOpenModal("")}
        />
      )}
    </div>
  );
};

export default DispatchHistory;
